import React, { useEffect, useMemo, Component } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import ScrollToTop from "./components/common/scrollToTop";
import PostHogPageviewTracker from "./components/common/PostHogPageviewTracker";
import { userDetails } from "./api/auth_api";
import { setUserProfile } from "./redux/store/userSlice";

import Login from "./pages/Auth/Login";
import SignUp from "./pages/Auth/SignUp";
import Verify from "./pages/Auth/Verify";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import ResetPassword from "./pages/Auth/ResetPassword";

import Layout from "./components/layout/layout";
import Dashboard from "./pages/MainMenu/Dashboard/dashboard";
import Analysis from "./pages/MainMenu/Analysis&News/analysis";
import News from "./pages/MainMenu/Analysis&News/news";
import MarketData from "./pages/MainMenu/MarketData/marketData";
import Settings from "./pages/Settings";
import AlertPreference from "./pages/Settings/alertPreference";
import Newsletter from "./pages/Settings/newsletter";
import AccountDetails from "./pages/Settings/accountDetails";
import AllPortfolio from "./pages/MainMenu/Portfolio/allPortfolio";
import TopStocks from "./pages/MainMenu/Find&Compare/topStocks";
import Watchlist from "./pages/MainMenu/Watchlist/watchlist";
import RecentlyViewed from "./pages/MainMenu/RecentlyViewed/recentlyViewed";
import StockDetails from "./pages/IndividualStocks/stockDetails";

import "./App.css";
// import HealthCheck from "./pages/MainMenu/Portfolio/portfolioHealth";
import EarningsCalendar from "./pages/MainMenu/Dashboard/earningsCalendar";
import QuantFactorRating from "./pages/MainMenu/Find&Compare/quantFactorRating";
import ETFsScreener from "./pages/MainMenu/Find&Compare/etfScreener";
import TopETFs from "./pages/MainMenu/Find&Compare/topEtfs";
import Comparison from "./pages/MainMenu/Find&Compare/comparison";
import StocksScreenerByQuants from "./pages/MainMenu/Find&Compare/stocksScreener_quant";
import StocksScreenerByRatings from "./pages/MainMenu/Find&Compare/stocksScreener_ratios";
import PortfolioHealthCheck from "./pages/MainMenu/Portfolio/portfolioHealth";
import EfficientFrontierSimulator from "./pages/MainMenu/Find&Compare/efficientFrontierSim";
import PortfolioDetail from "./pages/MainMenu/Portfolio/PorfolioDetail";

import { MarketProvider } from "./context/MarketContext";

import { useTheme } from './context/ThemeContext';
import { createAppTheme } from './theme';
import LandingPage from "./pages/Auth/LandingPage";

/* ── Error Boundary — prevents blank white screen on render errors (8.2.2) ── */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    console.error("ErrorBoundary caught:", error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          minHeight: "100vh", padding: "2rem", fontFamily: "inherit",
          backgroundColor: "var(--color-bg-primary, #F8FAFC)", color: "var(--color-text-primary, #000)",
        }}>
          <h1 style={{ fontSize: "1.5rem", marginBottom: "1rem" }}>Something went wrong</h1>
          <p style={{ color: "var(--color-text-secondary, #6B7280)", marginBottom: "1.5rem", textAlign: "center", maxWidth: 480 }}>
            An unexpected error occurred. Please reload the page to continue.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.625rem 1.5rem", borderRadius: "0.5rem", border: "none", cursor: "pointer",
              backgroundColor: "var(--color-primary, #0096FF)", color: "#fff",
              fontSize: "0.875rem", fontWeight: 600,
            }}
          >
            Reload page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const profile = useSelector((state) => state.user.profile);

  // Create Material-UI theme based on current theme
  const muiTheme = useMemo(() => createAppTheme(theme), [theme]);

  // Re-fetch user profile on refresh if token exists but Redux state was lost
  useEffect(() => {
    const token = sessionStorage.getItem("authToken");
    if (token && !profile) {
      userDetails().then((res) => {
        if (res.success) {
          dispatch(setUserProfile(res.data));
        }
      });
    }
  }, [dispatch, profile]);
  // Suppress ResizeObserver warning
  console.error = ((orig) => {
    return (...args) => {
      if (
        typeof args[0] === "string" &&
        args[0].includes("ResizeObserver loop")
      ) {
        return;
      }
      orig(...args);
    };
  })(console.error);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  return (
    <ErrorBoundary>
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <Router>
        <MarketProvider>   {/* ← moved inside Router */}
          <PostHogPageviewTracker />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Navigate to="/landingPage" replace />} />
            <Route path="/landingPage" element={<LandingPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/verify" element={<Verify />} />
            <Route path="/forgotPassword" element={<ForgotPassword />} />
            <Route path="/reset" element={<ResetPassword />} />
            <Route element={<Layout />}>
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/analysis" element={<Analysis />} />
              <Route path="/news" element={<News />} />
              <Route path="/marketData" element={<MarketData />} />
              <Route path="/settings" element={<Settings />}>
                <Route index element={<AccountDetails />} />
                <Route path="alerts" element={<AlertPreference />} />
                <Route path="newsletters" element={<Newsletter />} />
              </Route>
              <Route path="/allPortfolio" element={<AllPortfolio />} />
              <Route path="/recentlyViewed" element={<RecentlyViewed />} />
              <Route path="/watchlist" element={<Watchlist />} />
              {/* <Route path="/health" element={<HealthCheck />} /> */}
              <Route path="/comparison" element={<Comparison />} />
              <Route path="/portfolioHealth" element={<PortfolioHealthCheck />} />
              <Route path="/quantRating" element={<QuantFactorRating />} />
              <Route path="/frontierSimulator" element={<EfficientFrontierSimulator />} />
              <Route path="/stocksScreenerbyQuants" element={<StocksScreenerByQuants />} />
              <Route path="/stocksScreenerbyRatios" element={<StocksScreenerByRatings />} />
              <Route path="/etfsScreener" element={<ETFsScreener />} />
              <Route path="/topStocks" element={<TopStocks />} />
              <Route path="/topETFs" element={<TopETFs />} />
              <Route
                path="/earnings-calendar"
                element={<EarningsCalendar />}
              />

              {/* ---------- STOCK DETAILS (FIXED) ---------- */}
              <Route
                path="/stocks/:ticker/details"
                element={<Navigate to="summary" replace />}
              />
              <Route path="/portfolio/:portfolioId" element={<PortfolioDetail />} />
              <Route
                path="/stocks/:ticker/details/:tab"
                element={<StockDetails />}
              />
            </Route>

            {/* Optional 404 */}
            {/* <Route path="*" element={<Navigate to="/login" replace />} /> */}
          </Routes>
        </MarketProvider>
      </Router>
    </MuiThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
